import InputInterface from '~/modules/game-engine/input/interface';
import { MicInput } from '~/modules/game-engine/input/mic-input';
import { SelectedPlayerInput } from '~/modules/players/players-manager';
import { MicrophoneInputSource } from '~/routes/select-input/input-sources/microphone';

/**
 * How many channels of this device have to be split out. Driven by the highest channel anybody is
 * on rather than by whether two players share the device: `getPlayerFrequency` reads
 * `frequencies[input.channel]`, so a singer alone on the Red end of a SingStar pair was reading
 * `frequencies[1]` of a mono open, which nothing ever fills.
 *
 * Only the players actually on this microphone count. Every player is created on `Dummy` with
 * `deviceId: 'default'`, so matching on the device id alone let an unclaimed slot decide how a real
 * device was opened.
 */
const channelsToOpen = (allInputs: SelectedPlayerInput[] = [], deviceId: string | undefined) => {
  const channels = allInputs
    .filter((input) => input.source === MicrophoneInputSource.inputName && input.deviceId === deviceId)
    .map((input) => input.channel);

  return channels.some((channel) => channel > 0) ? 2 : 1;
};

class MultiMicInput implements InputInterface {
  private devices: Record<string, InputInterface> = {};
  public startMonitoring = async (deviceId?: string, allInputs?: SelectedPlayerInput[]) => {
    if (deviceId) {
      if (!this.devices[deviceId]) {
        this.devices[deviceId] = new MicInput(channelsToOpen(allInputs, deviceId));
      }
      await this.devices[deviceId].startMonitoring(deviceId);
    }
  };

  public getFrequencies = (deviceId?: string) => {
    if (deviceId && this.devices[deviceId]) {
      return this.devices[deviceId].getFrequencies();
    }
    return [0, 0];
  };

  public getVolumes = (deviceId?: string) => {
    if (deviceId && this.devices[deviceId]) {
      return this.devices[deviceId].getVolumes();
    }
    return [0, 0];
  };
  public clearFrequencies = (deviceId?: string) => {
    if (deviceId && this.devices[deviceId]) {
      return this.devices[deviceId].clearFrequencies(deviceId);
    }
  };
  public stopMonitoring = async () => {
    // Emptied before awaiting, not after: `InputManager` calls this once per player, so two singers
    // on one device close it twice over, and clearing at the end also erased any device opened while
    // the close was still in flight.
    const closing = Object.values(this.devices);
    this.devices = {};

    await Promise.all(closing.map((device) => device.stopMonitoring()));
  };

  public getInputLag = (deviceId?: string) => {
    if (deviceId && this.devices[deviceId]) {
      return this.devices[deviceId].getInputLag();
    }

    return 180;
  };
  public requestReadiness = async (deviceId?: string, secondsLeft?: number) => {
    if (deviceId && this.devices[deviceId]) {
      return this.devices[deviceId].requestReadiness(undefined, secondsLeft);
    }

    return true;
  };

  public getStatus = (deviceId?: string) => {
    if (deviceId && this.devices[deviceId]) {
      return this.devices[deviceId].getStatus();
    }
    return 'ok' as const;
  };
}

export default new MultiMicInput();
